import { Editor } from '@tiptap/core'
import { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { TextSelection } from '@tiptap/pm/state'
import { OutlineItem } from '../../../calls.service'
import { SentenceIdPair } from './ai-action.types'

export function getSelectedText(editor: Editor): string | null {
  const { from, to } = editor.state.selection

  if (from === to) {
    return null
  }

  return editor.state.doc.textBetween(from, to, ' ')
}

export function getSentenceIdPairsFromSelection(editor: Editor, outlineItems: OutlineItem[]): SentenceIdPair[] {
  const { from, to } = editor.state.selection
  const outlineItemsByNodeId = new Map(outlineItems.map((item) => [item.id.toString(), item]))

  const pairs: SentenceIdPair[] = []
  const seen = new Set<string>()

  editor.state.doc.nodesBetween(from, to, (node) => {
    if (node.type.name === 'listItem' && node.attrs['nodeId'] !== null) {
      const outlineItem = outlineItemsByNodeId.get(node.attrs['nodeId']) ?? null

      if (outlineItem !== null) {
        const key = `${outlineItem.startSentenceId}:${outlineItem.endSentenceId}`

        if (!seen.has(key)) {
          seen.add(key)
          pairs.push({
            startSentenceId: outlineItem.startSentenceId,
            endSentenceId: outlineItem.endSentenceId,
          })
        }
      }
    }
  })

  return pairs
}

export function getTemplateItemIdsFromSelection(editor: Editor): number[] {
  const { from, to } = editor.state.selection
  const templateItemIds = new Set<number>()

  editor.state.doc.nodesBetween(from, to, (node) => {
    if (node.type.name === 'listItem' && node.attrs['templateItemId'] !== null) {
      const parsed = Number(node.attrs['templateItemId'])

      if (!Number.isNaN(parsed)) {
        templateItemIds.add(parsed)
      }
    }
  })

  return [...templateItemIds]
}

export function expandSelectionToWordBoundaries(editor: Editor) {
  const { from, to } = editor.state.selection

  if (from === to) {
    throw new Error('The selection is empty.')
  }

  const isWordCharacter = (char: string) => /[\w'\-.!?]/.test(char)

  let expandedFrom = from
  let expandedTo = to

  while (expandedFrom > 0) {
    const char = editor.state.doc.textBetween(expandedFrom - 1, expandedFrom)
    if (char === '' || !isWordCharacter(char)) {
      break
    }
    expandedFrom--
  }

  const docSize = editor.state.doc.content.size
  while (expandedTo < docSize) {
    const char = editor.state.doc.textBetween(expandedTo, expandedTo + 1)
    if (char === '' || !isWordCharacter(char)) {
      break
    }
    expandedTo++
  }

  if (expandedFrom !== from || expandedTo !== to) {
    const transaction = editor.state.tr.setSelection(TextSelection.create(editor.state.doc, expandedFrom, expandedTo))
    editor.view.dispatch(transaction)
  }
}

export function getExistingItemContent(editor: Editor): string | null {
  const { $from } = editor.state.selection

  for (let depth = $from.depth; depth >= 0; depth--) {
    const node = $from.node(depth)

    if (node.type.name === 'listItem' && node.attrs['nodeId'] !== null) {
      return collectOwnItemText(node, '')
    }
  }

  return null
}

function collectOwnItemText(itemNode: ProseMirrorNode, numberPrefix: string): string {
  const isOrderedList = itemNode.type.name === 'orderedList'
  const isBulletList = itemNode.type.name === 'bulletList'
  let listItemIndex = 0

  const parts: { text: string; isBlock: boolean }[] = []

  itemNode.content.forEach((child) => {
    if (child.isText) {
      if ((child.text ?? '').length > 0) {
        parts.push({ text: child.text ?? '', isBlock: false })
      }
      return
    }

    if ((isOrderedList || isBulletList) && child.type.name === 'listItem') {
      listItemIndex++

      if (isOrderedList) {
        const number = numberPrefix.length > 0 ? `${numberPrefix}.${listItemIndex}` : `${listItemIndex}`
        const childText = collectOwnItemText(child, number)
        if (childText.length > 0) {
          parts.push({ text: `${number}. ${childText}`, isBlock: true })
        }
      } else {
        const childText = collectOwnItemText(child, numberPrefix)
        if (childText.length > 0) {
          parts.push({ text: `- ${childText}`, isBlock: true })
        }
      }
      return
    }

    const childText = collectOwnItemText(child, numberPrefix)
    if (childText.length > 0) {
      parts.push({ text: childText, isBlock: true })
    }
  })

  return parts.reduce((text, part, index) => {
    if (index === 0) {
      return part.text
    }
    const separator = part.isBlock || parts[index - 1].isBlock ? '\n' : ' '
    return `${text}${separator}${part.text}`
  }, '')
}
